import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
// formik
import { Field, FieldProps, Form, Formik, useFormikContext } from 'formik';
// validation
import { userVolunteerInfoFormValidationSchema } from 'core/validation';
import { API } from 'core/API';
import { isPhoneNumberValid, normalizePhoneNumber } from 'core/functions';
// redux
import { updateUser } from 'redux/companiesUsers-service/actions';
import { setError } from 'redux/error-service/action';
// context
import { UserTableDataContext } from 'pages/Admin/SuperAdminUserList/UsersTable';
// types
import { IUpdateUserRequest, ValidateUserPhoneNumberRequest } from '@joc/api-gateway';
// components
import ButtonDefault from 'shared/components/Buttons/ButtonsDefault';
import InputContainer from 'shared/components/FormInputs/InputContainer';
import InputPhone from 'shared/components/FormInputs/InputPhone';
import InputText from 'shared/components/FormInputs/InputText';
import GoogleApiAutoCompleat from 'shared/components/GoogleApiAutoComplete';
import InputErrorHint from 'shared/components/InputErrorHint';
import Loader from 'shared/components/Loader';
import InlineDatePicker from 'shared/components/Pickers/InlineDatePicker';
import GenderSelect from 'shared/components/Selects/SelectDropdown/GenderSelect';
import UploadUserPhoto from 'components/Volunteer/UploadUserPhoto';
// styles
import styles from './UserInfoForm.module.scss';

type UserInfoFormParentProps = {
	setIsUpdateSuccess: React.Dispatch<React.SetStateAction<boolean>>;
	isDisabled?: boolean;
};

const DUPLICATE_PHONE_ERROR = 'Phone number already exists';
const PHONE_VALIDATION_DEBOUNCE_MS = 400;

type FormValues = IUpdateUserRequest & { phoneNumber?: string };

const UserInfoForm: FC<UserInfoFormProps> = ({
	setIsUpdateSuccess,
	setError,
	updateUser,
	isDisabled,
}: UserInfoFormProps) => {
	const [isLoading, setIsLoading] = useState(false);
	const [phoneValidationError, setPhoneValidationError] = useState('');
	const [isPhoneValidationPending, setIsPhoneValidationPending] = useState(false);
	const { userData } = useContext(UserTableDataContext);
	const initialPhoneNumber = useMemo(() => normalizePhoneNumber(userData.phoneNumber), [userData.phoneNumber]);

	const validatePhoneUniqueness = async (normalizedPhoneNumber: string): Promise<string> => {
		console.log(normalizedPhoneNumber);
		if (!normalizedPhoneNumber) {
			return '';
		}

		if (!normalizedPhoneNumber || normalizedPhoneNumber === initialPhoneNumber) {
			return '';
		}
		if (!isPhoneNumberValid(normalizedPhoneNumber)) {
			return '';
		}

		try {
			await API.validatePhoneNumber(
				ValidateUserPhoneNumberRequest.fromJS({ phoneNumber: normalizedPhoneNumber })
			);
			return '';
		} catch (error: any) {
			if (error?.response?.code === 409 || error?.response?.status === 409) {
				return DUPLICATE_PHONE_ERROR;
			}
			throw error;
		}
	};

	const PhoneUniquenessValidator: FC = () => {
		const { values, setFieldError, setFieldTouched } = useFormikContext<FormValues>();
		const requestIdRef = useRef(0);

		useEffect(() => {
			const normalizedPhoneNumber = normalizePhoneNumber(values.phoneNumber);
			const isPhoneChanged = normalizedPhoneNumber !== initialPhoneNumber;
			const shouldValidatePhone =
				!!normalizedPhoneNumber && isPhoneChanged && isPhoneNumberValid(normalizedPhoneNumber);

			setPhoneValidationError('');
			setIsPhoneValidationPending(false);
			setFieldError('phoneNumber', '');

			if (!shouldValidatePhone) {
				return;
			}

			requestIdRef.current += 1;
			const requestId = requestIdRef.current;
			const timeoutId = window.setTimeout(async () => {
				setIsPhoneValidationPending(true);
				try {
					const errorMessage = await validatePhoneUniqueness(normalizedPhoneNumber);
					if (requestIdRef.current !== requestId) return;

					setPhoneValidationError(errorMessage);
					setFieldError('phoneNumber', errorMessage);
					if (errorMessage) setFieldTouched('phoneNumber', true, false);
				} catch (error: any) {
					if (requestIdRef.current !== requestId) return;
					setPhoneValidationError(error?.response?.message || error?.message || 'Phone validation failed');
				} finally {
					if (requestIdRef.current === requestId) {
						setIsPhoneValidationPending(false);
					}
				}
			}, PHONE_VALIDATION_DEBOUNCE_MS);

			return () => {
				window.clearTimeout(timeoutId);
			};
		}, [setFieldError, setFieldTouched, values.phoneNumber]);

		return null;
	};

	const initialValues = useMemo(() => {
		return {
			firstName: userData.firstName,
			lastName: userData.lastName,
			email: userData.email || '',
			phoneNumber: userData.phoneNumber,
			birthDate: userData.birthDate,
			gender: userData.gender?.id,
			imagePath: userData.imagePath,
			address: userData?.address,
		};
	}, [userData]);

	const submitClickHandler = async (values: IUpdateUserRequest) => {
		try {
			setIsLoading(true);
			const normalizedPhoneNumber = normalizePhoneNumber(values.phoneNumber);
			const phoneValidationErrorResponse = await validatePhoneUniqueness(normalizedPhoneNumber);

			if (phoneValidationErrorResponse) {
				setPhoneValidationError(phoneValidationErrorResponse);
				return;
			}

			await updateUser(+userData.id, {
				...values,
				email: values.email?.trim() || undefined,
				phoneNumber: normalizedPhoneNumber || undefined,
			});
			setIsUpdateSuccess(true);
		} catch (error: any) {
			setError(error?.response?.message || error.response, error?.response?.code || error.code);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Formik
			enableReinitialize
			initialValues={initialValues}
			validationSchema={userVolunteerInfoFormValidationSchema}
			onSubmit={(values): Promise<void> => submitClickHandler(values)}
		>
			{({ errors, touched, setFieldValue, setFieldTouched, dirty }) => (
				<Form className={styles.form}>
					<PhoneUniquenessValidator />
					<Field name="imagePath">
						{({ field }: FieldProps) => (
							<InputContainer parentClassnames={styles.upload_photo}>
								<UploadUserPhoto
									setFieldValue={setFieldValue}
									setFieldTouched={setFieldTouched}
									fieldName={field.name}
									fieldValue={field.value}
								/>
							</InputContainer>
						)}
					</Field>
					<Field name="firstName">
						{({ field }: FieldProps) => (
							<InputContainer>
								<label className={styles.label} htmlFor="firstName">
									First Name
								</label>
								<InputText
									id="firstName"
									field={field}
									placeholder="my name is..."
									fieldValue={field.value}
									setFieldValue={setFieldValue}
								/>
								{errors.firstName && touched.firstName && (
									<InputErrorHint errorText={errors.firstName} />
								)}
							</InputContainer>
						)}
					</Field>
					<Field name="lastName">
						{({ field }: FieldProps) => (
							<InputContainer>
								<label className={styles.label} htmlFor="lastName">
									Last Name
								</label>
								<InputText
									id="lastName"
									field={field}
									placeholder="fill in..."
									fieldValue={field.value}
									setFieldValue={setFieldValue}
								/>
								{errors.lastName && touched.lastName && <InputErrorHint errorText={errors.lastName} />}
							</InputContainer>
						)}
					</Field>
					<Field name="email">
						{({ field }: FieldProps) => (
							<InputContainer>
								<label className={styles.label} htmlFor="email">
									Email
								</label>
								<InputText
									id="email"
									field={field}
									placeholder="fill in..."
									fieldValue={field.value}
									setFieldValue={setFieldValue}
									isDisabled={isDisabled}
								/>
								{errors.email && touched.email && <InputErrorHint errorText={errors.email} />}
							</InputContainer>
						)}
					</Field>
					<Field name="phoneNumber">
						{({ field }: FieldProps) => (
							<InputContainer>
								<label className={styles.label} htmlFor="phoneNumber">
									Phone number
								</label>
								<InputPhone
									fieldName={field.name}
									fieldValue={field.value}
									setFieldValue={(fieldName, value) => {
										setPhoneValidationError('');
										setFieldValue(fieldName, value);
									}}
									setFieldTouched={setFieldTouched}
								/>
								{(phoneValidationError || errors.phoneNumber) && touched.phoneNumber && (
									<InputErrorHint errorText={phoneValidationError || errors.phoneNumber} />
								)}
							</InputContainer>
						)}
					</Field>
					<Field name="birthDate">
						{({ field }: FieldProps) => (
							<InputContainer isDisabledMargin isSmallHeight>
								<InlineDatePicker
									fieldName={field.name}
									setFieldValue={setFieldValue}
									parentClassNames={styles.birthDate}
									fieldValue={field.value}
									placeholder="Date of birth"
									availablePast
									isDisabled={isDisabled}
								/>
								{errors.birthDate && touched.birthDate && (
									<InputErrorHint errorText={errors.birthDate as string} />
								)}
							</InputContainer>
						)}
					</Field>
					<Field name="gender">
						{({ field }: FieldProps) => (
							<InputContainer isDisabledMargin isSmallHeight>
								<GenderSelect
									placeholder="Gender"
									setFieldTouched={setFieldTouched}
									setFieldValue={setFieldValue}
									fieldName={field.name}
									fieldValue={field.value}
									parentClassName={styles.gender}
									isDisabled={isDisabled}
								/>
								{errors.gender && touched.gender && <InputErrorHint errorText={errors.gender} />}
							</InputContainer>
						)}
					</Field>
					<Field name="address">
						{({ field }: FieldProps) => (
							<InputContainer isDisabledMargin isSmallHeight>
								<GoogleApiAutoCompleat
									fieldName={field.name}
									setFieldTouched={setFieldTouched}
									setFieldValue={setFieldValue}
									fieldValue={field.value}
								/>
								{errors.address && touched.address && (
									<InputErrorHint addressErrorText={errors.address} />
								)}
							</InputContainer>
						)}
					</Field>

					{isLoading ? (
						<Loader loadProps={{ disableCenterStyle: true }} />
					) : (
						<ButtonDefault
							submitType
							classList={['primary', 'lg']}
							title="Confirm"
							disabled={!dirty || !!phoneValidationError || isPhoneValidationPending}
						/>
					)}
				</Form>
			)}
		</Formik>
	);
};

const mapDispatchToProps = { updateUser, setError };

const connector = connect(null, mapDispatchToProps);

type UserInfoFormProps = ConnectedProps<typeof connector> & UserInfoFormParentProps;

export default connector(UserInfoForm);
